/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { jsx } from '@atlaskit/css';
import { Stack } from '@atlaskit/primitives/compiled';
import {
	BFFConsumerEnum,
	BFFProvider,
	RequestStatus,
	useBFFDataProvider,
} from '@atlassian/navigation-bff-data-provider';
import { MenuList } from '@atlassian/navigation-system/side-nav/menu-list';

import { ANALYTICS_CHANNEL } from '../constants';
import type {
	GlobalShortcutsItem,
	GlobalShortcutsProps,
	MappedGlobalShortcutsItem,
} from '../types';
import { GlobalShortcutsWithIntlProvider } from '../utils/locale';

import { ImageFromUrl } from './image-from-url';
import { MenuSkeletonItem } from './menu-skeleton-item';
import { ShortcutMenuItem } from './shortcut-menu-item';

function Menu({
	testId,
	shortcutItems = [],
	forceStatic,
	cloudId,
	orgId,
	useSkeleton,
	promoteSite,
	productLinksFilter,
	product,
}: GlobalShortcutsProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { buildURLAndFetchData, status, productLinks } = useBFFDataProvider({
		request: {},
		cloudId,
		orgId,
		forceStatic,
	});

	const mappedShortcutItems: Array<MappedGlobalShortcutsItem> = useMemo(() => {
		return shortcutItems.map((item, index) => {
			return {
				...item,
				key: item.apsKey || `shortcut-${index}`,
			};
		});
	}, [shortcutItems]);

	const apsKeys = useMemo(() => {
		return shortcutItems.map((item) => item.apsKey || '').filter((item) => !!item);
	}, [shortcutItems]);

	const mountedRef = useRef(false);

	useEffect(() => {
		if (
			forceStatic ||
			status?.toLowerCase() !== RequestStatus.COMPLETE ||
			!Object.keys(productLinks).length ||
			mountedRef.current === true
		) {
			return;
		}

		mountedRef.current = true;
		const missingShortcuts = shortcutItems.filter((item) => {
			return !item.url && !item.fallbackUrl?.() && item.apsKey && !productLinks[item.apsKey];
		});
		const intended = shortcutItems.length;
		const rendered = intended - missingShortcuts.length;

		createAnalyticsEvent({
			eventType: UI_EVENT_TYPE,
			action: 'cls',
			actionSubject: 'menu',
			actionSubjectId: 'globalNavigation',
			attributes: {
				name: product,
				intended,
				rendered,
				missingAPSKeys: missingShortcuts.map((item) => item.apsKey).filter(Boolean),
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, shortcutItems, productLinks, product, forceStatic, status]);

	useEffect(() => {
		if (!forceStatic) {
			buildURLAndFetchData(apsKeys);
		}
	}, [apsKeys, buildURLAndFetchData, forceStatic]);

	const createLinkItem = useCallback(
		(shortcut: MappedGlobalShortcutsItem) => {
			// Render nested items if the dynamic data is available and we are not forcing static rendering
			if (
				!forceStatic &&
				status?.toLowerCase() === RequestStatus.COMPLETE &&
				shortcut?.apsKey &&
				productLinks?.[shortcut.apsKey]
			) {
				let filteredProductLinks = productLinks[shortcut.apsKey];

				if (productLinksFilter) {
					filteredProductLinks = filteredProductLinks.filter(productLinksFilter);
				}

				const siteName = filteredProductLinks?.[0]?.displayName;

				// Determine whether shortcut is a single site and is current site and return link instead of flyout
				if (filteredProductLinks.length === 1) {
					if (!shortcut.url) {
						shortcut.url = filteredProductLinks[0].url;
					}

					return (
						<ShortcutMenuItem
							key={shortcut.key}
							shortcut={shortcut}
							status={status}
							siteName={siteName}
						/>
					);
				}

				// Determine promoted site and filter out from list
				const sites = filteredProductLinks.reduce<{
					promoted?: GlobalShortcutsItem;
					items: (GlobalShortcutsItem & { displayName: string })[];
				}>(
					(acc, item) => {
						// Check if the item is a promoted site and filter it out
						if (promoteSite && item.isCurrentSite) {
							acc.promoted = {
								icon: <ImageFromUrl src={item.avatar} alt="" fallback={shortcut.icon} />,
								label: item.displayName,
								url: item.url,
							};
							return acc;
						}
						acc.items.push({
							icon: <ImageFromUrl src={item.avatar} alt="" fallback={shortcut.icon} />,
							label: item.displayName,
							displayName: item.displayName, // used for sorting items
							url: item.url,
						});
						return acc;
					},
					{ promoted: undefined, items: [] },
				);

				return (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						status={status}
						expandItems={sites.items}
						promotedItem={sites.promoted}
						siteName={siteName}
					/>
				);
			}

			// Show skeleton while BFF is loading
			if (useSkeleton && (status === RequestStatus.LOADING || status === RequestStatus.INIT)) {
				return <MenuSkeletonItem key={shortcut.key} />;
			}

			// We only want to allow static menu expand item if we are forcing static,
			// otherwise it should be showing the singular item as a disabled link until the
			// data is loaded.
			if (forceStatic && shortcut.items) {
				return (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						expandItems={shortcut.items}
						forceStatic={forceStatic}
					/>
				);
			}

			return (
				<ShortcutMenuItem
					key={shortcut.key}
					shortcut={shortcut}
					status={status}
					forceStatic={forceStatic}
				/>
			);
		},
		[forceStatic, status, productLinks, useSkeleton, productLinksFilter, promoteSite],
	);

	return (
		<Stack testId={testId}>
			<MenuList>{mappedShortcutItems?.map(createLinkItem)}</MenuList>
		</Stack>
	);
}

export default function GlobalShortcuts(props: GlobalShortcutsProps) {
	return (
		<BFFProvider consumer={BFFConsumerEnum.GLOBAL_SHORTCUTS}>
			<GlobalShortcutsWithIntlProvider>
				<NavigationAnalyticsContext data={{ product: props?.product }}>
					<Menu {...props} />
				</NavigationAnalyticsContext>
			</GlobalShortcutsWithIntlProvider>
		</BFFProvider>
	);
}
